import React, { useState, useEffect } from 'react';
import { 
  X, 
  FileText, 
  User, 
  Camera, 
  PenTool, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle,
  ArrowRight,
  Download,
  Eye,
  MessageSquare,
  Shield,
  Calendar,
  Hash,
  Mail,
  Award
} from 'lucide-react';
import { useUser } from '../../store/useAuthStore';
import { apiClient } from '../../lib/api';
import { formatShortDate, formatDateTime } from '../../utils/dateUtils';

interface PhysicianIDProofingViewerProps {
  physicianId: string;
  /**
   * Render inline instead of as its own overlay.
   *
   * The account modal used to launch this on top of itself — two stacked
   * dialogs for one account, with a close button that dropped you back into
   * another modal. Embedded, the review lives in the account it belongs to.
   */
  embedded?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onActionComplete?: () => void;
}

interface NpiAddress {
  addressType: string;
  addressPurpose?: string;
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  telephoneNumber?: string;
}

interface NpiTaxonomy {
  code?: string;
  description?: string;
  state?: string;
  license?: string;
  primary?: boolean;
}

interface PhysicianDocuments {
  physician: {
    id: string;
    name: string;
    firstName?: string;
    lastName?: string;
    email: string;
    phone?: string;
    clinicName?: string;
    clinicAddress?: { street?: string; street2?: string; city?: string; state?: string; zipCode?: string };
    npiNumber: string;
    status: string;
    idProofingStatus: string;
    submittedAt?: string;
    verifiedAt?: string;
    verificationAttempts: number;
    // Telemed-only clinic (no NPI/license): Gate B is satisfied by the signed
    // Contract + BAA, not a credential verification.
    telemedOnly?: boolean;
    agreementAccepted?: boolean;
  };
  npiRegistryData?: {
    firstName: string;
    lastName: string;
    credential?: string;
    status?: string;
    enumerationDate?: string;
    organizationName?: string;
    addresses?: NpiAddress[];
    taxonomies?: NpiTaxonomy[];
    fetchedAt?: string;
    notFound?: boolean;
  } | null;
  documents: {
    driversLicense?: {
      fileKey: string;
      uploadedAt: string;
      downloadUrl: string;
    };
    selfie?: {
      fileKey: string;
      uploadedAt: string;
      downloadUrl: string;
    };
    signature?: {
      fileKey: string;
      uploadedAt: string;
      downloadUrl: string;
    };
  };
  approvalHistory: {
    approvedBy?: string;
    approvalNotes?: string;
    rejectedBy?: string;
    rejectionReason?: string;
    rejectionNotes?: string;
    rejectedAt?: string;
  };
  credentialVerification: {
    status: string;
    verifiedAt?: string;
    expiresAt?: string;
    licenseState?: string;
    licenseNumber?: string;
    notes?: string;
    verifiedBy?: { email: string; name: string; role: string } | null;
  } | null;
  verificationHistory: Array<{
    action: string;
    previousStatus: string;
    newStatus: string;
    changedAt: string;
    reason?: string;
    notes?: string;
    changedBy?: { email: string; name: string; role: string } | null;
  }>;
}

// Two-step verification stepper. Visually anchors the modal so admins
// understand they're working through ID Proofing → License Verification.
// Step 2 stays "Locked" until Step 1 reaches `approved` (mirrors backend
// gate logic; the credential form below is also hidden until then).
type StepStatus = 'pending' | 'active' | 'done' | 'rejected' | 'locked' | 'expired' | 'revoked';

interface VerificationStepperProps {
  idStatus: string;
  licenseStatus?: string;
}

const stepStatusFromIdProofing = (s: string): StepStatus => {
  switch (s) {
    case 'approved': return 'done';
    case 'rejected': return 'rejected';
    case 'submitted': return 'active';
    default: return 'pending';
  }
};

const stepStatusFromLicense = (idStatus: string, licenseStatus?: string): StepStatus => {
  if (idStatus !== 'approved') return 'locked';
  if (!licenseStatus) return 'pending';
  switch (licenseStatus) {
    case 'verified': return 'done';
    case 'expired': return 'expired';
    case 'revoked': return 'revoked';
    default: return 'active';
  }
};

const stepLabel = (s: StepStatus): { text: string; pillClass: string; iconClass: string } => {
  switch (s) {
    case 'done':     return { text: 'Approved', pillClass: 'bg-green-100 text-green-800',  iconClass: 'bg-green-600 text-white' };
    case 'active':   return { text: 'In review', pillClass: 'bg-blue-100 text-blue-800',   iconClass: 'bg-blue-600 text-white' };
    case 'pending':  return { text: 'Awaiting submission', pillClass: 'bg-gray-100 text-gray-700', iconClass: 'bg-gray-400 text-white' };
    case 'rejected': return { text: 'Rejected', pillClass: 'bg-red-100 text-red-800',     iconClass: 'bg-red-600 text-white' };
    case 'locked':   return { text: 'Unlocks after Step 1', pillClass: 'bg-gray-100 text-gray-500', iconClass: 'bg-gray-300 text-white' };
    case 'expired':  return { text: 'Expired',  pillClass: 'bg-amber-100 text-amber-800',  iconClass: 'bg-amber-600 text-white' };
    case 'revoked':  return { text: 'Revoked',  pillClass: 'bg-red-100 text-red-800',      iconClass: 'bg-red-600 text-white' };
  }
};

/**
 * The single next action, spelled out.
 *
 * The stepper shows STATE; a reviewer opening this modal still had to infer
 * what they were supposed to DO — most sharply when step 2 read "Locked" with
 * no reason and no step-2 section rendered at all. This says it outright.
 */
function nextAction(idStatus: string, licenseStatus?: string): {
  title: string; detail: string; tone: 'act' | 'wait' | 'done' | 'warn';
} {
  if (idStatus === 'rejected') {
    return { tone: 'warn', title: 'Step 1 was rejected',
      detail: 'The clinic must re-submit their documents before anything else can happen.' };
  }
  if (idStatus === 'submitted') {
    return { tone: 'act', title: 'Your turn — review the ID documents',
      detail: 'Check the licence photo against the selfie and the NPI registry name below, then approve or reject at the end of Step 1.' };
  }
  if (idStatus !== 'approved') {
    return { tone: 'wait', title: 'Waiting on the clinic',
      detail: 'They have not uploaded their ID documents yet. Nothing to review until they do.' };
  }
  // Step 1 approved from here on.
  if (!licenseStatus) {
    return { tone: 'act', title: 'Your turn — verify the state licence',
      detail: 'Step 1 is approved. Confirm the licence with the state board, then record it in Step 2 below.' };
  }
  if (licenseStatus === 'expired') {
    return { tone: 'warn', title: 'Licence on file has expired',
      detail: 'This account cannot transact until the licence is renewed and re-recorded in Step 2.' };
  }
  if (licenseStatus === 'revoked') {
    return { tone: 'warn', title: 'Licence on file is revoked',
      detail: 'This account cannot transact. Escalate rather than re-recording it.' };
  }
  if (licenseStatus === 'verified') {
    return { tone: 'done', title: 'Both steps complete',
      detail: 'Identity confirmed and licence verified — this account can place orders.' };
  }
  return { tone: 'act', title: 'Your turn — finish licence verification',
    detail: 'Confirm the licence with the state board, then record it in Step 2 below.' };
}

const NextActionBar: React.FC<{ idStatus: string; licenseStatus?: string }> = ({ idStatus, licenseStatus }) => {
  const { title, detail, tone } = nextAction(idStatus, licenseStatus);
  const styles = {
    act:  'bg-blue-50 border-blue-200 text-blue-900',
    wait: 'bg-gray-50 border-gray-200 text-gray-700',
    done: 'bg-green-50 border-green-200 text-green-900',
    warn: 'bg-amber-50 border-amber-200 text-amber-900'
  }[tone];
  const Icon = tone === 'done' ? CheckCircle : tone === 'warn' ? AlertTriangle : tone === 'wait' ? Clock : ArrowRight;
  return (
    <div className={`flex items-start gap-3 px-4 py-3 border rounded-lg ${styles}`}>
      <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
      <div>
        <div className="font-semibold text-sm">{title}</div>
        <div className="text-sm opacity-90 mt-0.5">{detail}</div>
      </div>
    </div>
  );
};

const StepIndicator: React.FC<{ index: number; status: StepStatus }> = ({ index, status }) => {
  const { iconClass } = stepLabel(status);
  return (
    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${iconClass}`}>
      {status === 'done'
        ? <CheckCircle className="w-5 h-5" />
        : status === 'rejected' || status === 'revoked'
          ? <XCircle className="w-5 h-5" />
          : index}
    </div>
  );
};

const VerificationStepper: React.FC<VerificationStepperProps> = ({ idStatus, licenseStatus }) => {
  const step1 = stepStatusFromIdProofing(idStatus);
  const step2 = stepStatusFromLicense(idStatus, licenseStatus);
  const step1Label = stepLabel(step1);
  const step2Label = stepLabel(step2);
  return (
    <div className="flex items-center gap-4">
      {/* Step 1: ID Proofing */}
      <div className="flex items-center gap-3 flex-1">
        <StepIndicator index={1} status={step1} />
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900">Step 1 · ID Proofing</div>
          <div className={`mt-0.5 inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${step1Label.pillClass}`}>
            {step1Label.text}
          </div>
        </div>
      </div>
      {/* Connector */}
      <div className={`hidden sm:block flex-1 h-px ${step1 === 'done' ? 'bg-green-300' : 'bg-gray-300'}`} />
      {/* Step 2: License Verification */}
      <div className="flex items-center gap-3 flex-1">
        <StepIndicator index={2} status={step2} />
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900">Step 2 · License Verification</div>
          <div className={`mt-0.5 inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${step2Label.pillClass}`}>
            {step2Label.text}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Outer frame: a plain bordered panel when embedded in a host modal, an overlay
 * dialog when standing alone. Embedded there is no overlay and no outer close
 * button, and the host modal owns the scrolling — otherwise the two compete and
 * the inner pane traps the wheel.
 *
 * This must stay at module scope. It was previously built inside the component's
 * render, which meant every render produced a new function, and React compares
 * element types by reference — so a different type on each render tore down the
 * entire subtree and rebuilt it. The visible effect was that typing anywhere in
 * the screen, the Step 2 licence fields included, unmounted the field on the
 * first keystroke and dropped the cursor: one character per click.
 */
const ViewerShell: React.FC<{ embedded: boolean; children: React.ReactNode }> = ({
  embedded,
  children
}) =>
  embedded ? (
    <div className="border border-gray-200 rounded-lg overflow-hidden">{children}</div>
  ) : (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {children}
      </div>
    </div>
  );

/**
 * One uploaded document. Module scope on purpose — defined inside the viewer's
 * render it became a new component type every render, so React tore down and
 * rebuilt all three cards on each keystroke. Same reason as ViewerShell above.
 */
const DocumentCard: React.FC<{
  title: string;
  icon: React.ComponentType<any>;
  document?: { fileKey: string; uploadedAt: string; downloadUrl: string };
  description: string;
  onOpen: (url: string, filename: string) => void;
}> = ({ title, icon: Icon, document, description, onOpen }) => (
  <div className="border border-gray-200 rounded-lg p-4">
    <div className="flex items-center mb-3">
      <Icon className="w-5 h-5 text-blue-600 mr-2" />
      <h4 className="font-medium text-gray-900">{title}</h4>
    </div>
    <p className="text-sm text-gray-600 mb-3">{description}</p>
    
    {document ? (
      <div className="space-y-2">
        <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-800">Uploaded</span>
          </div>
          <div className="text-xs text-green-600">
            {formatShortDate(document.uploadedAt)}
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => onOpen(document.downloadUrl, `${title.toLowerCase().replace(' ', '_')}.pdf`)}
            className="flex-1 flex items-center justify-center px-3 py-2 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
          >
            <Eye className="w-4 h-4 mr-2" />
            View
          </button>
          <button
            onClick={() => onOpen(document.downloadUrl, `${title.toLowerCase().replace(' ', '_')}.pdf`)}
            className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            Download
          </button>
        </div>
      </div>
    ) : (
      <div className="flex items-center p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <AlertTriangle className="w-4 h-4 text-gray-500 mr-2" />
        <span className="text-sm text-gray-600">Not uploaded</span>
      </div>
    )}
  </div>
  );

const PhysicianIDProofingViewer: React.FC<PhysicianIDProofingViewerProps> = ({
  physicianId,
  embedded = false,
  isOpen,
  onClose,
  onActionComplete
}) => {
  const user = useUser();
  const [data, setData] = useState<PhysicianDocuments | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [showForceVerifyForm, setShowForceVerifyForm] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [forceVerifyNotes, setForceVerifyNotes] = useState('');
  const [showCredentialForm, setShowCredentialForm] = useState(false);
  const [credentialLicenseState, setCredentialLicenseState] = useState('');
  const [credentialLicenseNumber, setCredentialLicenseNumber] = useState('');
  const [credentialNotes, setCredentialNotes] = useState('');
  const [npiRefreshing, setNpiRefreshing] = useState(false);

  const handleRefreshNpi = async () => {
    try {
      setNpiRefreshing(true);
      const response = await apiClient.refreshPhysicianNpiLookup(physicianId);
      if (response.success) {
        await fetchDocuments();
      } else {
        alert(`Failed to refresh: ${response.error}`);
      }
    } finally {
      setNpiRefreshing(false);
    }
  };

  const normalizeName = (s?: string) =>
    (s || '').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/\s+(md|do|np|pa|rn)$/i, '').trim();

  useEffect(() => {
    if (isOpen && physicianId) {
      fetchDocuments();
    }
  }, [isOpen, physicianId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getPhysicianDocuments(physicianId);
      if (response.success && response.data) {
        setData(response.data);
      } else {
        console.error('Failed to fetch documents:', response.error);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!data) return;
    
    try {
      setActionLoading('approve');
      const response = await apiClient.approvePhysicianVerification(physicianId, approvalNotes);
      if (response.success) {
        alert('Physician verification approved successfully!');
        onActionComplete?.();
        onClose();
      } else {
        alert(`Failed to approve: ${response.error}`);
      }
    } catch (error) {
      console.error('Error approving physician:', error);
      alert('Failed to approve physician verification');
    } finally {
      setActionLoading('');
      setShowApprovalForm(false);
      setApprovalNotes('');
    }
  };

  const handleReject = async () => {
    if (!data || !rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    
    try {
      setActionLoading('reject');
      const response = await apiClient.rejectPhysicianVerification(physicianId, rejectionReason, rejectionNotes);
      if (response.success) {
        alert('Physician verification rejected successfully');
        onActionComplete?.();
        onClose();
      } else {
        alert(`Failed to reject: ${response.error}`);
      }
    } catch (error) {
      console.error('Error rejecting physician:', error);
      alert('Failed to reject physician verification');
    } finally {
      setActionLoading('');
      setShowRejectionForm(false);
      setRejectionReason('');
      setRejectionNotes('');
    }
  };

  const handleVerifyCredentials = async () => {
    if (!credentialLicenseState.trim() || !credentialLicenseNumber.trim()) {
      alert('License state and license number are required');
      return;
    }
    try {
      setActionLoading('verify-credentials');
      const response = await apiClient.verifyPhysicianCredentials(physicianId, {
        licenseState: credentialLicenseState.trim(),
        licenseNumber: credentialLicenseNumber.trim(),
        notes: credentialNotes.trim() || undefined
      });
      if (response.success) {
        alert('Credentials verified successfully');
        setShowCredentialForm(false);
        setCredentialLicenseState('');
        setCredentialLicenseNumber('');
        setCredentialNotes('');
        await fetchDocuments();
        onActionComplete?.();
      } else {
        alert(`Failed to verify credentials: ${response.error}`);
      }
    } catch (error) {
      console.error('Error verifying credentials:', error);
      alert('Failed to verify credentials');
    } finally {
      setActionLoading('');
    }
  };

  const handleForceVerify = async () => {
    if (!data) return;

    try {
      setActionLoading('force-verify');
      const response = await apiClient.forceVerifyPhysician(physicianId, forceVerifyNotes);
      if (response.success) {
        alert('Physician has been force-verified successfully!');
        onActionComplete?.();
        onClose();
      } else {
        alert(`Failed to force verify: ${response.error}`);
      }
    } catch (error) {
      console.error('Error force verifying physician:', error);
      alert('Failed to force verify physician');
    } finally {
      setActionLoading('');
      setShowForceVerifyForm(false);
      setForceVerifyNotes('');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: 'bg-gray-100 text-gray-800', icon: Clock, label: 'Pending Upload' },
      submitted: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Awaiting Review' },
      verified: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Verified' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4 mr-2" />
        {config.label}
      </span>
    );
  };

  const openDocument = (url: string, filename: string) => {
    // Open document in new tab with secure URL
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  return (
    <ViewerShell embedded={embedded}>
      <>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <Shield className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Clinic Verification</h2>
              <p className="text-sm text-gray-600">Two-step review: ID proofing, then license verification</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Embedded, the host modal owns closing — a second X here just
                offers to shut the panel you came to look at. */}
            {!embedded && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            )}
          </div>
        </div>

        {/* Verification stepper — surfaces the two gates a physician must
            clear before they can place orders. ID Proofing must complete
            before License Verification unlocks (mirrors backend gate logic). */}
        {data && (
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
            <VerificationStepper
              idStatus={data.physician.idProofingStatus}
              licenseStatus={data.credentialVerification?.status}
            />
            <div className="mt-3">
              <NextActionBar
                idStatus={data.physician.idProofingStatus}
                licenseStatus={data.credentialVerification?.status}
              />
            </div>
          </div>
        )}

        <div className="overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : data ? (
            <div className="p-6 space-y-6">
              {/* Physician Info */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                    <User className="w-5 h-5 mr-2 text-blue-600" />
                    Clinic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Name</label>
                      <p className="text-gray-900">{data.physician.name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Email</label>
                      <p className="text-gray-900 flex items-center">
                        <Mail className="w-4 h-4 mr-1 text-gray-400" />
                        {data.physician.email}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">NPI Number</label>
                      <p className="text-gray-900 flex items-center">
                        <Hash className="w-4 h-4 mr-1 text-gray-400" />
                        {data.physician.npiNumber}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Verification Attempts</label>
                      <p className="text-gray-900">{data.physician.verificationAttempts} of 3</p>
                    </div>
                    {data.physician.submittedAt && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Submitted</label>
                        <p className="text-gray-900 flex items-center">
                          <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                          {formatShortDate(data.physician.submittedAt)}
                        </p>
                      </div>
                    )}
                    {data.physician.verifiedAt && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Verified</label>
                        <p className="text-gray-900 flex items-center">
                          <Award className="w-4 h-4 mr-1 text-green-600" />
                          {formatShortDate(data.physician.verifiedAt)}
                        </p>
                      </div>
                    )}
                    {data.physician.phone && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Phone</label>
                        <p className="text-gray-900">{data.physician.phone}</p>
                      </div>
                    )}
                    {data.physician.clinicName && (
                      <div className="md:col-span-2">
                        <label className="text-sm font-medium text-gray-500">Clinic</label>
                        <p className="text-gray-900">{data.physician.clinicName}</p>
                      </div>
                    )}
                    {data.physician.clinicAddress && (data.physician.clinicAddress.street || data.physician.clinicAddress.city) && (
                      <div className="md:col-span-2">
                        <label className="text-sm font-medium text-gray-500">Clinic Address</label>
                        <p className="text-gray-900 text-sm">
                          {[
                            [data.physician.clinicAddress.street, data.physician.clinicAddress.street2].filter(Boolean).join(', '),
                            [data.physician.clinicAddress.city, data.physician.clinicAddress.state, data.physician.clinicAddress.zipCode].filter(Boolean).join(', ')
                          ].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Verification Status</h3>
                  <div className="space-y-3">
                    {getStatusBadge(data.physician.idProofingStatus)}
                    <div className="text-sm text-gray-600">
                      <p><strong>Account Status:</strong> {data.physician.status}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* NPI Registry Lookup */}
              {(() => {
                const reg = data.npiRegistryData;
                const enteredFirst = normalizeName(data.physician.firstName);
                const enteredLast = normalizeName(data.physician.lastName);
                const regFirst = normalizeName(reg?.firstName);
                const regLast = normalizeName(reg?.lastName);
                const firstMatch = !!reg && !reg.notFound && enteredFirst && regFirst && enteredFirst === regFirst;
                const lastMatch = !!reg && !reg.notFound && enteredLast && regLast && enteredLast === regLast;
                const fullMatch = firstMatch && lastMatch;
                const partialMatch = !fullMatch && (firstMatch || lastMatch);

                let chip: React.ReactNode = null;
                if (!reg) {
                  chip = (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                      <Clock className="w-3 h-3 mr-1" />Not yet looked up
                    </span>
                  );
                } else if (reg.notFound) {
                  chip = (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">
                      <AlertTriangle className="w-3 h-3 mr-1" />Not in registry
                    </span>
                  );
                } else if (fullMatch) {
                  chip = (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">
                      <CheckCircle className="w-3 h-3 mr-1" />Name matches
                    </span>
                  );
                } else if (partialMatch) {
                  chip = (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800">
                      <AlertTriangle className="w-3 h-3 mr-1" />Partial match
                    </span>
                  );
                } else {
                  chip = (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">
                      <XCircle className="w-3 h-3 mr-1" />Name mismatch
                    </span>
                  );
                }

                const primaryTax = reg?.taxonomies?.find(t => t.primary) || reg?.taxonomies?.[0];
                const primaryAddr = reg?.addresses?.find(a => a.addressType === 'LOCATION') || reg?.addresses?.[0];

                return (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3 flex items-center justify-between">
                      <span className="flex items-center">
                        <Hash className="w-5 h-5 mr-2 text-blue-600" />
                        NPI Registry (CMS)
                      </span>
                      <button
                        onClick={handleRefreshNpi}
                        disabled={npiRefreshing}
                        className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                      >
                        {npiRefreshing ? 'Refreshing…' : 'Refresh'}
                      </button>
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-3">
                        {chip}
                        {reg?.status && reg.status !== 'A' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">
                            Status: {reg.status}
                          </span>
                        )}
                        {reg?.fetchedAt && (
                          <span className="text-xs text-gray-500">
                            looked up {formatDateTime(reg.fetchedAt)}
                          </span>
                        )}
                      </div>
                      {!reg ? (
                        <p className="text-sm text-gray-600 italic">No lookup data yet — click Refresh to fetch from CMS.</p>
                      ) : reg.notFound ? (
                        <p className="text-sm text-red-700">
                          NPI <span className="font-mono">{data.physician.npiNumber}</span> was not found in the CMS registry. Verify the number entered at registration.
                        </p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <div className="text-xs text-gray-500 uppercase tracking-wide">Registry Name</div>
                            <div className={`mt-0.5 ${fullMatch ? 'text-gray-900' : partialMatch ? 'text-amber-800' : 'text-red-700 font-medium'}`}>
                              {[reg.firstName, reg.lastName].filter(Boolean).join(' ')}
                              {reg.credential ? `, ${reg.credential}` : ''}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              entered: {[data.physician.firstName, data.physician.lastName].filter(Boolean).join(' ') || data.physician.name}
                            </div>
                          </div>
                          {primaryTax && (
                            <div>
                              <div className="text-xs text-gray-500 uppercase tracking-wide">Primary Taxonomy</div>
                              <div className="mt-0.5">{primaryTax.description || primaryTax.code}</div>
                              {(primaryTax.license || primaryTax.state) && (
                                <div className="text-xs text-gray-700 mt-0.5">
                                  License: <span className="font-mono">{[primaryTax.state, primaryTax.license].filter(Boolean).join(' · ')}</span>
                                </div>
                              )}
                            </div>
                          )}
                          {primaryAddr && (
                            <div className="md:col-span-2">
                              <div className="text-xs text-gray-500 uppercase tracking-wide">Practice Address</div>
                              <div className="mt-0.5">
                                {[primaryAddr.street1, primaryAddr.street2].filter(Boolean).join(', ')}
                                {primaryAddr.street1 ? ' · ' : ''}
                                {[primaryAddr.city, primaryAddr.state, primaryAddr.postalCode].filter(Boolean).join(', ')}
                              </div>
                              {primaryAddr.telephoneNumber && (
                                <div className="text-xs text-gray-700 mt-0.5">Phone: {primaryAddr.telephoneNumber}</div>
                              )}
                            </div>
                          )}
                          {reg.taxonomies && reg.taxonomies.length > 1 && (
                            <div className="md:col-span-2">
                              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">All Licenses</div>
                              <div className="space-y-0.5 text-xs text-gray-700 font-mono">
                                {reg.taxonomies.map((t, i) => (
                                  <div key={i}>
                                    {t.primary ? '★ ' : '  '}{[t.state, t.license].filter(Boolean).join(' · ') || '—'} <span className="font-sans text-gray-500">{t.description || t.code}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Step 1 — ID Proofing (Gate A) */}
              <div>
                <h3 className="font-medium text-gray-900 mb-1 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-blue-600" />
                  Step 1 · ID Proofing
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  Confirm the physician's identity from the documents they uploaded.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <DocumentCard
                    title="Drivers License"
                    icon={FileText}
                    document={data.documents.driversLicense}
                    description="Government-issued photo ID for identity verification"
                    onOpen={openDocument}
                  />
                  <DocumentCard
                    title="Selfie Photo"
                    icon={Camera}
                    document={data.documents.selfie}
                    description="Self-captured photo to match with drivers license"
                    onOpen={openDocument}
                  />
                  <DocumentCard
                    title="E-Signature"
                    icon={PenTool}
                    document={data.documents.signature}
                    description="Digital signature acknowledging terms and conditions"
                    onOpen={openDocument}
                  />
                </div>
              </div>

              {/* Approval History */}
              {(data.approvalHistory.approvedBy || data.approvalHistory.rejectedBy) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                    <MessageSquare className="w-5 h-5 mr-2 text-blue-600" />
                    Approval History
                  </h3>
                  <div className="space-y-3">
                    {data.approvalHistory.approvedBy && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-green-800">✅ Approved</p>
                        <p className="text-sm text-green-700">By: {data.approvalHistory.approvedBy}</p>
                        {data.approvalHistory.approvalNotes && (
                          <p className="text-sm text-green-600 mt-1">Notes: {data.approvalHistory.approvalNotes}</p>
                        )}
                      </div>
                    )}
                    {data.approvalHistory.rejectedBy && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-red-800">❌ Rejected</p>
                        <p className="text-sm text-red-700">By: {data.approvalHistory.rejectedBy}</p>
                        <p className="text-sm text-red-700">Reason: {data.approvalHistory.rejectionReason}</p>
                        {data.approvalHistory.rejectionNotes && (
                          <p className="text-sm text-red-600 mt-1">Notes: {data.approvalHistory.rejectionNotes}</p>
                        )}
                        {data.approvalHistory.rejectedAt && (
                          <p className="text-xs text-red-500 mt-1">
                            {formatDateTime(data.approvalHistory.rejectedAt)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons — visible whenever the docs are in (or the
                  physician explicitly hit "submit"). Some physicians upload
                  all three but never click submit, leaving status `pending`
                  even though admin/AM can already review. */}
              {(() => {
                const hasAllDocs = !!(
                  data.documents.driversLicense &&
                  data.documents.selfie &&
                  data.documents.signature
                );
                const reviewable =
                  data.physician.idProofingStatus === 'submitted' ||
                  (data.physician.idProofingStatus !== 'approved' && hasAllDocs);
                return reviewable && (user?.role === 'distributor' || user?.role === 'admin');
              })() && (
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      {/* These buttons decide STEP 1 only. Sitting under the
                          documents with no label, they read as a verdict on the
                          whole physician — approving here does not verify the
                          licence, it unlocks the step that does. */}
                      <p className="font-medium text-gray-900">Step 1 decision · ID Proofing</p>
                      <p className="mt-0.5">
                        Approving confirms identity and the clinician’s direct email and phone, including contact review for pending partner onboarding. It unlocks Step 2; the clinician still accepts the partner connection.
                      </p>
                    </div>
                    <div className="flex space-x-3">
                      {/* Approve Button */}
                      {!showApprovalForm ? (
                        <button
                          onClick={() => setShowApprovalForm(true)}
                          disabled={actionLoading !== ''}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve
                        </button>
                      ) : (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 min-w-80">
                          <h4 className="font-medium text-green-800 mb-2">Approve Verification</h4>
                          <p className="text-sm text-green-800 mb-2">Confirm that the email and phone on this account belong directly to the clinician.</p>
                          <textarea
                            placeholder="Optional approval notes..."
                            value={approvalNotes}
                            onChange={(e) => setApprovalNotes(e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                          <div className="flex space-x-2 mt-3">
                            <button
                              onClick={handleApprove}
                              disabled={actionLoading === 'approve'}
                              className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
                            >
                              {actionLoading === 'approve' ? 'Approving...' : 'Confirm Approval'}
                            </button>
                            <button
                              onClick={() => {
                                setShowApprovalForm(false);
                                setApprovalNotes('');
                              }}
                              className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Reject Button (Admin Only) */}
                      {user?.role === 'admin' && !showRejectionForm ? (
                        <button
                          onClick={() => setShowRejectionForm(true)}
                          disabled={actionLoading !== ''}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject
                        </button>
                      ) : user?.role === 'admin' && showRejectionForm ? (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 min-w-80">
                          <h4 className="font-medium text-red-800 mb-2">Reject Verification</h4>
                          <input
                            type="text"
                            placeholder="Reason for rejection (required)"
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mb-2"
                          />
                          <textarea
                            placeholder="Additional notes (optional)"
                            value={rejectionNotes}
                            onChange={(e) => setRejectionNotes(e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                          <div className="flex space-x-2 mt-3">
                            <button
                              onClick={handleReject}
                              disabled={actionLoading === 'reject' || !rejectionReason.trim()}
                              className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50"
                            >
                              {actionLoading === 'reject' ? 'Rejecting...' : 'Confirm Rejection'}
                            </button>
                            <button
                              onClick={() => {
                                setShowRejectionForm(false);
                                setRejectionReason('');
                                setRejectionNotes('');
                              }}
                              className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2 — Gate B. Telemed-only clinics satisfy it via the signed
                  Contract + BAA (no NPI/license to verify); everyone else needs a
                  credential/license verification. */}
              {data.physician.telemedOnly ? (
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="font-medium text-gray-900 mb-1 flex items-center">
                    <Award className="w-5 h-5 mr-2 text-purple-600" />
                    Step 2 · Telemed Clinic (Contract + BAA)
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    This is a telemed-only clinic with no NPI — it prescribes through the doctor network.
                    Gate B is satisfied by the signed Clinic Services Agreement &amp; BAA, not a medical license.
                  </p>
                  {data.physician.agreementAccepted ? (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm text-purple-800 flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>Contract &amp; BAA signed — this clinic is verified via agreement (no license required).</span>
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                      Awaiting the clinic's signed Contract &amp; BAA. Once signed and ID proofing is approved, the clinic is verified — there is no license to confirm.
                    </div>
                  )}
                </div>
              ) : !(data.credentialVerification || data.physician.idProofingStatus === 'approved') ? (
                /* Step 2 used to disappear entirely here, which made the
                   stepper's locked pill read as a dead end. Show the step, and
                   say plainly what opens it. */
                <div className="border-t border-gray-200 pt-6 opacity-70">
                  <h3 className="font-medium text-gray-500 mb-1 flex items-center">
                    <Award className="w-5 h-5 mr-2 text-gray-400" />
                    Step 2 · License Verification
                  </h3>
                  <div className="mt-3 border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
                    <p className="text-sm text-gray-600">
                      Opens once Step 1 is approved. The state licence is checked against the
                      board and recorded here — the account cannot place orders until both
                      steps are complete.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="font-medium text-gray-900 mb-1 flex items-center">
                    <Award className="w-5 h-5 mr-2 text-blue-600" />
                    Step 2 · License Verification
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Confirm the physician's medical license with the state board, then record the details.
                  </p>
                  {data.credentialVerification ? (
                    <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Status</div>
                        <div className={`font-medium mt-0.5 ${
                          data.credentialVerification.status === 'verified' ? 'text-green-700'
                          : data.credentialVerification.status === 'expired' ? 'text-amber-700'
                          : data.credentialVerification.status === 'revoked' ? 'text-red-700'
                          : 'text-gray-700'
                        }`}>
                          {data.credentialVerification.status}
                        </div>
                      </div>
                      {data.credentialVerification.verifiedAt && (
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wide">Verified</div>
                          <div className="mt-0.5">{formatDateTime(data.credentialVerification.verifiedAt)}</div>
                        </div>
                      )}
                      {data.credentialVerification.expiresAt && (
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wide">Expires</div>
                          <div className={`mt-0.5 ${new Date(data.credentialVerification.expiresAt) < new Date() ? 'text-red-600 font-medium' : ''}`}>
                            {formatShortDate(data.credentialVerification.expiresAt)}
                          </div>
                        </div>
                      )}
                      {data.credentialVerification.verifiedBy && (
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wide">Verified by</div>
                          <div className="mt-0.5">{data.credentialVerification.verifiedBy.name}</div>
                        </div>
                      )}
                      {(data.credentialVerification.licenseState || data.credentialVerification.licenseNumber) && (
                        <div className="col-span-2">
                          <div className="text-xs text-gray-500 uppercase tracking-wide">License</div>
                          <div className="mt-0.5">
                            {[data.credentialVerification.licenseState, data.credentialVerification.licenseNumber].filter(Boolean).join(' · ') || '—'}
                          </div>
                        </div>
                      )}
                      {data.credentialVerification.notes && (
                        <div className="col-span-2">
                          <div className="text-xs text-gray-500 uppercase tracking-wide">Notes</div>
                          <div className="mt-0.5 text-gray-700 italic">{data.credentialVerification.notes}</div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                      Credentials not yet verified. ID proofing is approved (Gate A), but the physician's license still needs to be confirmed before they can place orders.
                    </div>
                  )}

                  {user?.role === 'admin' && data.credentialVerification?.status !== 'verified' && (
                    <div className="mt-3">
                      {!showCredentialForm ? (
                        <button
                          onClick={() => setShowCredentialForm(true)}
                          disabled={actionLoading !== ''}
                          className="inline-flex items-center px-3 py-1.5 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Verify Credentials
                        </button>
                      ) : (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h4 className="font-medium text-green-800 mb-2">Verify Physician Credentials</h4>
                          <p className="text-xs text-green-700 mb-3">
                            Confirm the physician's license with the state board, then enter the details below. This sets Gate B and allows them to place orders for 6 months.
                          </p>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">License State</label>
                              <input
                                type="text"
                                placeholder="e.g. GA"
                                maxLength={2}
                                value={credentialLicenseState}
                                onChange={(e) => setCredentialLicenseState(e.target.value.toUpperCase())}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm uppercase"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">License Number</label>
                              <input
                                type="text"
                                placeholder="License #"
                                value={credentialLicenseNumber}
                                onChange={(e) => setCredentialLicenseNumber(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                              />
                            </div>
                          </div>
                          <div className="mt-3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
                            <textarea
                              placeholder="e.g. Confirmed via state board lookup on 2026-04-28"
                              value={credentialNotes}
                              onChange={(e) => setCredentialNotes(e.target.value)}
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            />
                          </div>
                          <div className="flex space-x-2 mt-3">
                            <button
                              onClick={handleVerifyCredentials}
                              disabled={actionLoading === 'verify-credentials'}
                              className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
                            >
                              {actionLoading === 'verify-credentials' ? 'Verifying...' : 'Confirm Verification'}
                            </button>
                            <button
                              onClick={() => {
                                setShowCredentialForm(false);
                                setCredentialLicenseState('');
                                setCredentialLicenseNumber('');
                                setCredentialNotes('');
                              }}
                              className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Verification History */}
              {data.verificationHistory && data.verificationHistory.length > 0 && (
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <Clock className="w-4 h-4 mr-2 text-gray-500" />
                    Verification History ({data.verificationHistory.length})
                  </h3>
                  <div className="space-y-2">
                    {[...data.verificationHistory]
                      .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime())
                      .map((entry, i) => (
                        <div key={i} className="bg-gray-50 rounded-lg px-3 py-2 text-sm">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900">
                                {entry.action.replace(/_/g, ' ')}
                              </div>
                              <div className="text-xs text-gray-600 mt-0.5">
                                {entry.previousStatus} → <span className="font-medium">{entry.newStatus}</span>
                                {entry.changedBy && (
                                  <> · by {entry.changedBy.name} <span className="text-gray-400">({entry.changedBy.role})</span></>
                                )}
                              </div>
                              {(entry.reason || entry.notes) && (
                                <div className="text-xs text-gray-600 mt-1 italic">
                                  {[entry.reason, entry.notes].filter(Boolean).join(' — ')}
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 flex-shrink-0 whitespace-nowrap">
                              {formatDateTime(entry.changedAt)}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* The override, deliberately last and deliberately quiet.
                  It used to be a chip in the header, level with the title and
                  visible before the documents — the first thing an admin saw
                  was the button for skipping the review. It now sits after the
                  normal decision, as a plain link until asked for. */}
              {user?.role === 'admin' && !showForceVerifyForm && (
                <div className="border-t border-gray-200 pt-4 mt-2">
                  <button
                    onClick={() => setShowForceVerifyForm(true)}
                    disabled={actionLoading !== ''}
                    className="text-xs text-gray-500 hover:text-orange-700 underline underline-offset-2 disabled:opacity-50"
                  >
                    Can't complete the normal review? Force verify (admin override)
                  </button>
                </div>
              )}

              {user?.role === 'admin' && showForceVerifyForm && (
                <div className="border-t border-orange-200 pt-6">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h4 className="font-medium text-orange-800 mb-2 flex items-center">
                      <Shield className="w-4 h-4 mr-2 text-orange-600" />
                      Force Verify Clinic (Admin Override)
                    </h4>
                    <p className="text-xs text-orange-700 mb-3">
                      Marks both gates verified without completing normal ID proofing or license verification.
                    </p>
                    <textarea
                      placeholder="Reason for force verification (optional)"
                      value={forceVerifyNotes}
                      onChange={(e) => setForceVerifyNotes(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <div className="flex space-x-2 mt-3">
                      <button
                        onClick={handleForceVerify}
                        disabled={actionLoading === 'force-verify'}
                        className="px-3 py-1.5 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700 disabled:opacity-50"
                      >
                        {actionLoading === 'force-verify' ? 'Verifying...' : 'Confirm Force Verify'}
                      </button>
                      <button
                        onClick={() => {
                          setShowForceVerifyForm(false);
                          setForceVerifyNotes('');
                        }}
                        className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Data</h3>
                <p className="text-gray-600">Unable to fetch physician documents.</p>
              </div>
            </div>
          )}
        </div>
      </>
    </ViewerShell>
  );
};

export default PhysicianIDProofingViewer;